import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { productLines as defaultLines, companyInfo as defaultCompany } from '../data/products';

const DataContext = createContext();

export function DataProvider({ children }) {
  const [productLines, setProductLines] = useState([]);
  const [companyInfo, setCompanyInfo]   = useState(defaultCompany);
  const [loading, setLoading]           = useState(true);
  const [usingFirebase, setUsingFirebase] = useState(false);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const snap = await getDocs(collection(db, 'productLines'));
      if (!snap.empty) {
        const lines = snap.docs
          .map(d => ({ ...d.data(), _id: d.id }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setProductLines(lines);
        setUsingFirebase(true);
        const compSnap = await getDocs(collection(db, 'companyInfo'));
        if (!compSnap.empty) setCompanyInfo(compSnap.docs[0].data());
      } else {
        setProductLines(defaultLines);
      }
    } catch {
      setProductLines(defaultLines);
    } finally {
      setLoading(false);
    }
  }

  async function seedFirebase() {
    try {
      for (let i = 0; i < defaultLines.length; i++) {
        await setDoc(doc(db, 'productLines', defaultLines[i].id), { ...defaultLines[i], order: i });
      }
      await setDoc(doc(db, 'companyInfo', 'main'), defaultCompany);
      await loadData();
      return true;
    } catch { return false; }
  }

  async function updateLine(id, data) {
    if (usingFirebase) await setDoc(doc(db, 'productLines', id), data, { merge: true });
    setProductLines(prev => prev.map(l => (l.id === id || l._id === id) ? { ...l, ...data } : l));
  }

  async function addLine(lineData) {
    const id = lineData.name.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();
    const newLine = { ...lineData, id, order: productLines.length, products: [] };
    if (usingFirebase) await setDoc(doc(db, 'productLines', id), newLine);
    setProductLines(prev => [...prev, newLine]);
  }

  async function deleteLine(id) {
    if (usingFirebase) await deleteDoc(doc(db, 'productLines', id));
    setProductLines(prev => prev.filter(l => l.id !== id && l._id !== id));
  }

  async function updateProduct(lineId, idx, data) {
    const line = productLines.find(l => l.id === lineId || l._id === lineId);
    if (!line) return;
    const products = line.products.map((p, i) => i === idx ? { ...p, ...data } : p);
    await updateLine(lineId, { ...line, products });
  }

  async function addProduct(lineId, data) {
    const line = productLines.find(l => l.id === lineId || l._id === lineId);
    if (!line) return;
    await updateLine(lineId, { ...line, products: [...line.products, data] });
  }

  async function deleteProduct(lineId, idx) {
    const line = productLines.find(l => l.id === lineId || l._id === lineId);
    if (!line) return;
    await updateLine(lineId, { ...line, products: line.products.filter((_, i) => i !== idx) });
  }

  async function updateCompany(data) {
    if (usingFirebase) await setDoc(doc(db, 'companyInfo', 'main'), data, { merge: true });
    setCompanyInfo(prev => ({ ...prev, ...data }));
  }

  return (
    <DataContext.Provider value={{
      productLines, companyInfo, loading, usingFirebase,
      seedFirebase, updateLine, addLine, deleteLine,
      updateProduct, addProduct, deleteProduct, updateCompany, reload: loadData
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() { return useContext(DataContext); }
